<template>
  <div class="py-1 space-y-1">
    <!-- Priority Tier Badge -->
    <div v-if="maxPriorityTier" class="mb-1">
      <span class="px-2 py-0.5 rounded text-xs font-bold" :class="getPriorityBadgeClass(maxPriorityTier)">
        P{{ displayPriorityNumber(maxPriorityTier) }}
      </span>
    </div>

    <!-- Per-medication urgency display -->
    <div v-for="med in medicationsWithDates" :key="med.measure_code">
      <div class="flex items-center gap-1 relative"
           :class="{ 'bg-blue-50 rounded px-1 -mx-1': med.isNext }">

        <!-- Next indicator arrow -->
        <span v-if="med.isNext" class="absolute -left-3 text-blue-600 font-bold animate-pulse">
          &rarr;
        </span>

        <!-- Icon based on urgency -->
        <span :class="getIconClass(med.daysRemaining)">
          {{ getIcon(med.daysRemaining) }}
        </span>

        <!-- Medication type badge -->
        <span class="text-xs font-semibold"
              :class="med.isNext ? 'text-blue-700 bg-blue-100 px-1 rounded' : 'text-gray-600'">
          {{ med.measure_code }}
        </span>

        <!-- Date with color coding -->
        <span :class="getDateClass(med.daysRemaining)" class="text-xs">
          {{ formatDate(med.lastdaytofail) }}
        </span>

        <!-- Days remaining badge if critical -->
        <span v-if="med.daysRemaining !== null && med.daysRemaining <= 7 && med.daysRemaining >= 0"
              class="px-1 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">
          {{ med.daysRemaining }}d
        </span>

        <!-- Star indicator if this is the next one -->
        <span v-if="med.isNext" class="text-yellow-500 text-xs ml-auto">
          &#11088;
        </span>
      </div>
    </div>

    <!-- First fill medications (no fail date) -->
    <div v-for="med in firstFillMedications" :key="'ff-' + med.measure_code"
         class="flex items-center gap-1">
      <span class="text-blue-500">&#128197;</span>
      <span class="text-xs font-semibold text-blue-600 bg-blue-50 px-1 rounded">
        {{ med.measure_code }}
      </span>
      <span class="text-xs text-blue-600">
        <template v-if="med.next_filled_dt">
          Next fill {{ formatDate(med.next_filled_dt) }}
        </template>
        <template v-else>
          First fill
        </template>
      </span>
    </div>

    <!-- Empty state -->
    <div v-if="!medicationsWithDates.length && !firstFillMedications.length" class="text-xs text-gray-400 italic">
      No priority data
    </div>
  </div>
</template>

<script>
export default {
  name: 'MedAdherence_UrgencyCell',
  props: ['params'],
  computed: {
    medications() {
      return this.params.data?.medications || [];
    },

    maxPriorityTier() {
      return this.params.data?.priority_tier || null;
    },

    medicationsWithDates() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let earliestDate = null;
      let earliestCode = null;

      const meds = this.medications
        .filter(med => med.lastdaytofail && !med.has_already_failed)
        .map(med => {
          const impactableDate = new Date(med.lastdaytofail);
          impactableDate.setHours(0, 0, 0, 0);
          const daysRemaining = Math.ceil((impactableDate - today) / (1000 * 60 * 60 * 24));

          // Track earliest future date for "next" indicator
          if (daysRemaining >= 0 && (!earliestDate || impactableDate < earliestDate)) {
            earliestDate = impactableDate;
            earliestCode = med.measure_code;
          }

          return {
            ...med,
            daysRemaining,
            isNext: false
          };
        });

      // Mark the "next" medication
      if (earliestCode) {
        const nextMed = meds.find(m => m.measure_code === earliestCode);
        if (nextMed) nextMed.isNext = true;
      }

      // Add failed medications
      const failed = this.medications
        .filter(med => med.has_already_failed && med.lastdaytofail)
        .map(med => ({
          ...med,
          daysRemaining: null,
          isNext: false
        }));

      return [...meds, ...failed].sort((a, b) => {
        if (a.daysRemaining === null && b.daysRemaining === null) return 0;
        if (a.daysRemaining === null) return 1;
        if (b.daysRemaining === null) return -1;
        return a.daysRemaining - b.daysRemaining;
      });
    },

    firstFillMedications() {
      return this.medications.filter(med =>
        !med.lastdaytofail && !med.has_already_failed &&
        (med.fill_indicator === 'FIRSTFILL' || !med.pdc_value)
      );
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const [datePart] = dateString.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
      });
    },

    getIcon(daysRemaining) {
      if (daysRemaining === null) return '\u274C'; // Past due / failed
      if (daysRemaining < 0) return '\u274C';
      if (daysRemaining <= 7) return '\uD83D\uDEA8';
      if (daysRemaining <= 30) return '\u26A0\uFE0F';
      return '\uD83D\uDCC5';
    },

    getIconClass(daysRemaining) {
      if (daysRemaining === null) return 'text-gray-500';
      if (daysRemaining < 0) return 'text-gray-500';
      if (daysRemaining <= 7) return 'text-red-600';
      if (daysRemaining <= 30) return 'text-orange-500';
      if (daysRemaining <= 60) return 'text-yellow-500';
      return 'text-blue-500';
    },

    getDateClass(daysRemaining) {
      if (daysRemaining === null) return 'text-gray-500 line-through';
      if (daysRemaining < 0) return 'text-gray-500 line-through';
      if (daysRemaining <= 7) return 'text-red-700 font-semibold';
      if (daysRemaining <= 30) return 'text-orange-600 font-semibold';
      if (daysRemaining <= 60) return 'text-yellow-700';
      return 'text-gray-700';
    },

    displayPriorityNumber(tier) {
      // Invert: tier 5 = P1 (most urgent), tier 1 = P5 (routine)
      return 6 - tier;
    },

    getPriorityBadgeClass(tier) {
      const classes = {
        5: 'bg-red-600 text-white',
        4: 'bg-primary-500 text-white',
        3: 'bg-yellow-500 text-black',
        2: 'bg-primary-500 text-white',
        1: 'bg-gray-400 text-white'
      };
      return classes[tier] || 'bg-gray-300 text-gray-700';
    }
  }
};
</script>

<style scoped>
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>
